import React, { useMemo, useState } from 'react';
import {
  useTable,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';

import PaginatedTable from './PaginatedTable';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';
import { Link } from 'react-router';
import { formatPhoneNumbers } from '../helpers/formatters';
import useMediaQuery from '../helpers/useMediaQuery';
import type { Farm } from '../models/Farm';
import MobileList from './MobileList';

export default function FarmRosterTable({ farms }: { farms: Farm[] }) {
  const matches = useMediaQuery('screen and (max-width: 640px)');

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState('');

  const tableData = useMemo(() => farms, [farms]);

  const columnData: ColumnDef<AppTableFeatures, Farm>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 250,
        cell: ({ row }) => (
          <Link
            prefetch='viewport'
            to={`/farms/${row.original.farmId}`}
            className='font-bold hover:opacity-70 dark:text-gray-300'
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        header: 'Status',
        accessorFn: (d) => (d.active ? 'Active' : 'Archived'),
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 100,
      },
      {
        header: 'Alpacas',
        accessorFn: (d) => `${d?.alpacas?.length ?? 0}`,
        sortFn: 'alphanumeric',
        filterFn: 'equals',
        size: 100,
        // cell: ({ row }) => <span>{row.original.alpacas.length} Alpacas</span>,
      },
      {
        header: 'Address 1',
        accessorKey: 'address1',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'Address 2',
        accessorKey: 'address2',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'City',
        accessorKey: 'city',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'State',
        accessorFn: (d) => d.state?.abbreviation ?? '',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 75,
      },
      {
        header: 'ZIP Code',
        accessorKey: 'zipcode',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'Phone',
        accessorKey: 'phoneNumber',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 160,
        cell: ({ row }) =>
          row.original.phoneNumber
            ? formatPhoneNumbers(row.original.phoneNumber ?? '')
            : '',
      },
      {
        header: 'Email Address',
        accessorKey: 'email',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 225,
      },
    ],
    [],
  );

  const columns = useMemo(() => columnData, [columnData]);

  const table = useTable({
    features: appTableFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: farms.length > 9 ? 10 : farms.length,
      },
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    paginateExpandedRows: false,
  });

  if (matches) {
    return (
      <MobileList
        table={table}
        columnResizeMode='onChange'
        showFilters={true}
        showPagination={farms.length > 9}
      />
    );
  }
  return (
    <PaginatedTable
      table={table}
      columnResizeMode='onChange'
      showFilters={true}
      showPagination={farms.length > 9}
    />
  );
}
